import React from 'react';
import type { PictureImageUrls } from '../../hooks/usePictureImageLoad';
import LoadingSpinner from './LoadingSpinner';
import type { LoadingSpinnerProps } from './LoadingSpinner';

export interface PictureImageContentProps {
  imageUrls: PictureImageUrls;
  pictureKey: string;
  imgRef: React.RefObject<HTMLImageElement | null>;
  alt: string;
  lazy: boolean;
  isLoading: boolean;
  onLoad: () => void;
  onError: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  spinnerSize?: LoadingSpinnerProps['size'];
}

/**
 * Shared <picture> markup with loading overlay for portrait, banner, and title images.
 * An opaque overlay hides progressive decode; the image appears in one step when ready.
 */
export const PictureImageContent: React.FC<PictureImageContentProps> = ({
  imageUrls,
  pictureKey,
  imgRef,
  alt,
  lazy,
  isLoading,
  onLoad,
  onError,
  spinnerSize = 'sm',
}) => (
  <>
    <picture
      key={pictureKey}
      style={{ display: 'block', width: '100%', height: '100%', position: 'relative', zIndex: 0 }}
    >
      <source type="image/webp" srcSet={imageUrls.webp} />
      <img
        ref={imgRef}
        src={imageUrls.jpg}
        alt={alt}
        loading={lazy ? 'lazy' : 'eager'}
        decoding="async"
        onError={onError}
        onLoad={onLoad}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </picture>
    {isLoading && (
      <div
        className="absolute inset-0 z-10 flex items-center justify-center bg-gray-700"
        data-testid="picture-image-loading"
        aria-busy="true"
        aria-label="Loading image"
      >
        <LoadingSpinner size={spinnerSize} color="white" />
      </div>
    )}
  </>
);

export default PictureImageContent;
